'use client';

import type { TooltipProps } from './Tooltip.types';
import styles from './Tooltip.module.css';
import {
  cloneElement,
  createContext,
  forwardRef,
  isValidElement,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  useMergeRefs,
  FloatingPortal,
  arrow,
  FloatingArrow,
  useTransitionStyles,
  safePolygon,
} from '@floating-ui/react';
import { getClasses } from '@/_utils/classnames';

export function useTooltip({
  initialOpen = false,
  placement = 'top',
  isDisabled = false,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  variant,
}: TooltipProps = {}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);
  const arrowRef = useRef(null);

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({
        crossAxis: placement.includes('-'),
        fallbackAxisSideDirection: 'start',
        padding: 5,
      }),
      shift({ padding: 5 }),
      // `arrow` must be at end of array to ensure arrow is positioned at
      // centered of trigger element
      arrow({
        element: arrowRef,
        padding: 10, // ensures the arrow doesn't go beyond rounded tooltip corner
      }),
    ],
  });

  const context = data.context;

  const hover = useHover(context, {
    move: false,
    enabled: controlledOpen == null,
    handleClose: variant === 'glossary' ? safePolygon() : undefined,
  });
  const focus = useFocus(context, {
    enabled: controlledOpen == null,
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const interactions = useInteractions([hover, focus, dismiss, role]);

  return useMemo(
    () => ({
      arrowRef,
      isDisabled,
      open,
      setOpen,
      variant,
      ...interactions,
      ...data,
    }),
    [isDisabled, open, setOpen, interactions, data, variant],
  );
}

type ContextType = ReturnType<typeof useTooltip> | null;

const TooltipContext = createContext<ContextType>(null);

export const useTooltipContext = () => {
  const context = useContext(TooltipContext);

  if (context == null) {
    throw new Error('Tooltip components must be wrapped in <Tooltip />');
  }

  return context;
};

/**
 * Tooltip component that wraps the trigger and content components.
 *
 * Tooltip content should be static; use a Popover for dynamic content.
 *
 * @example
 * <Tooltip placement="right">
 *   <Tooltip.Trigger>
 *     <button>Hover here!</button>
 *   </Tooltip.Trigger>
 *   <Tooltip.Content>
 *     <Text type="small" color="currentcolor" isEllipsis={true}>
 *       Hello from the tooltip!
 *     </Text>
 *   </Tooltip.Content>
 * </Tooltip>
 */
export const Tooltip = ({
  children,
  ...options
}: { children: React.ReactNode } & TooltipProps) => {
  // This can accept any props as options, e.g. `placement`,
  // or other positioning options.
  const tooltip = useTooltip(options);
  return <TooltipContext value={tooltip}>{children}</TooltipContext>;
};

/**
 * Element to trigger the tooltip when hovered over.
 *
 * Trigger element must be refable (e.g., a native HTML element, a functional
 * component that forwards its ref, etc.); if not, the trigger element will be
 * wrapped in a div.
 *
 * @example
 * <Tooltip placement="right">
 *   <Tooltip.Trigger>
 *     <button>Hover here!</button>
 *   </Tooltip.Trigger>
 *   <Tooltip.Content>
 *     <Text type="small" color="currentcolor" isEllipsis={true}>
 *       Hello from the tooltip!
 *     </Text>
 *   </Tooltip.Content>
 * </Tooltip>
 */
Tooltip.Trigger = forwardRef<HTMLElement, React.HTMLProps<HTMLElement>>(
  function TooltipTrigger({ children }, propRef) {
    const context = useTooltipContext();

    const ref = useMergeRefs([context.refs.setReference, propRef]);

    if (children == null) return null; // avoid breaking UX for user in unexpected scenario

    const isChildRefable =
      isValidElement(children) &&
      'ref' in children &&
      (typeof children.type === 'string' || // child is native HTML element
        (typeof children.type === 'object' && // child is ForwardRefExoticComponent
          (children.type as any).$$typeof === Symbol.for('react.forward_ref')));

    if (!isValidElement(children)) {
      throw new Error('Tooltip.Trigger must have a single child element');
    }

    if (!isChildRefable) {
      return (
        <div ref={ref} data-state={context.open ? 'open' : 'closed'}>
          {children}
        </div>
      );
    }

    return cloneElement(
      children,
      context.getReferenceProps({
        ref,
        ...(children as any).props,
        // The user can style the trigger based on the state
        'data-state': context.open ? 'open' : 'closed',
      }),
    );
  },
);

/**
 * Content to be rendered inside the tooltip when it's shown. Can be text, or
 * any other element type.
 *
 * Tooltip content should be static; use a Popover for dynamic content.
 *
 * @example
 * <Tooltip placement="right">
 *   <Tooltip.Trigger>
 *     <button>Hover here!</button>
 *   </Tooltip.Trigger>
 *   <Tooltip.Content>
 *     <Text type="small" color="currentcolor" isEllipsis={true}>
 *       Hello from the tooltip!
 *     </Text>
 *   </Tooltip.Content>
 * </Tooltip>
 */
Tooltip.Content = forwardRef<HTMLDivElement, React.HTMLProps<HTMLDivElement>>(
  function TooltipContent({ style, className, ...props }, propRef) {
    const context = useTooltipContext();
    const ref = useMergeRefs([context.refs.setFloating, propRef]);

    const { isMounted, styles: transitionStyles } = useTransitionStyles(
      context.context,
      {
        duration: 80,
      },
    );

    if (!isMounted || context.isDisabled) return null;

    const getVariantClasses = (variant?: string) => {
      switch (variant) {
        case 'glossary':
          return styles.tooltipGlossary;
        case 'button':
          return styles.tooltipButtonVariant;
        case 'button-disabled':
          return getClasses(
            styles.tooltipButtonVariant,
            styles.tooltipButtonVariantDisabled,
          );
        case 'button-danger':
          return getClasses(
            styles.tooltipButtonVariant,
            styles.tooltipButtonVariantDanger,
          );
        case 'button-danger-disabled':
          return getClasses(
            styles.tooltipButtonVariant,
            styles.tooltipButtonVariantDisabledDanger,
          );
        default:
          return '';
      }
    };

    const getArrowClasses = (variant?: string) => {
      switch (variant) {
        default:
          return styles.tooltipArrowFill;
      }
    };

    const shouldShowArrow = (variant?: string) => {
      const noArrowVariants = [
        'button',
        'button-disabled',
        'button-danger',
        'button-danger-disabled',
        'glossary',
      ];
      return !noArrowVariants.includes(variant || '');
    };

    const tooltipContentClasses = getClasses(
      styles.tooltipContent,
      getVariantClasses(context.variant),
      className,
    );

    return (
      <FloatingPortal>
        <div
          ref={ref}
          className={tooltipContentClasses}
          style={{
            ...context.floatingStyles,
            ...style,
            ...transitionStyles,
          }}
          {...context.getFloatingProps(props)}
        >
          {props.children}
          {shouldShowArrow(context.variant) && (
            <FloatingArrow
              ref={context.arrowRef}
              context={context.context}
              tipRadius={2}
              className={getArrowClasses(context.variant)}
              style={{ transform: 'translateY(-1px)' }}
              fill="currentColor"
            />
          )}
        </div>
      </FloatingPortal>
    );
  },
);
